import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { CampaignItem, LinkItem } from '../types';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { QRCodeModal } from '../components/QRCodeModal';
import {
  ArrowLeft,
  Plus,
  Copy,
  ExternalLink,
  QrCode,
  Edit2,
  Layers,
  MousePointerClick,
  Link as LinkIcon,
  Tag,
} from 'lucide-react';
import { copyToClipboard, formatDate, formatNumber } from '../lib/utils';
import { useToast } from '../hooks/useToast';

interface CampaignDetailProps {
  campaignId: string;
  onNavigate: (path: string) => void;
  onOpenCreate: (presetCampaignId?: string) => void;
  onEditLink: (id: string) => void;
}

export const CampaignDetail: React.FC<CampaignDetailProps> = ({
  campaignId,
  onNavigate,
  onOpenCreate,
  onEditLink,
}) => {
  const { showToast } = useToast();
  const [campaign, setCampaign] = useState<CampaignItem | null>(null);
  const [links, setLinks] = useState<LinkItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [qrModalData, setQrModalData] = useState<{ shortCode: string; title: string } | null>(null);

  useEffect(() => {
    setIsLoading(true);
    api.get<{ campaign: CampaignItem; links: LinkItem[] }>(`/campaigns/${campaignId}`)
      .then((data) => {
        setCampaign(data.campaign);
        setLinks(data.links || []);
      })
      .catch((err) => {
        showToast('Failed to load campaign', err.message, 'error');
        onNavigate('/admin/campaigns');
      })
      .finally(() => setIsLoading(false));
  }, [campaignId, showToast, onNavigate]);

  const handleCopy = async (shortCode: string) => {
    const url = `${window.location.origin}/${shortCode}`;
    const ok = await copyToClipboard(url);
    if (ok) showToast('Copied to clipboard', url, 'success');
  };

  if (isLoading || !campaign) {
    return <div className="py-20 text-center text-slate-500 text-sm">Loading campaign details...</div>;
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Back button & Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => onNavigate('/admin/campaigns')}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Campaigns</span>
        </button>

        <Button
          variant="primary"
          size="sm"
          onClick={() => onOpenCreate(campaign.id)}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Add Link to Campaign
        </Button>
      </div>

      {/* Campaign Summary Card */}
      <Card className="bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 border-orange-500/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-orange-500/10 text-orange-400 border border-orange-500/20">
                <Layers className="w-4 h-4" />
              </span>
              <h1 className="text-xl font-bold text-white tracking-tight">{campaign.name}</h1>
            </div>
            {campaign.description && (
              <p className="text-xs text-slate-400 max-w-2xl">{campaign.description}</p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-[10px] text-slate-500 uppercase font-semibold block">Total Links</span>
              <span className="font-mono font-bold text-slate-100 text-lg">{links.length}</span>
            </div>
            <div className="text-right pl-4 border-l border-slate-800">
              <span className="text-[10px] text-slate-500 uppercase font-semibold block">Total Combined Clicks</span>
              <span className="font-mono font-bold text-orange-400 text-lg">{formatNumber(campaign.total_clicks)}</span>
            </div>
          </div>
        </div>

        {/* UTM tags preset indicator */}
        {(campaign.utm_source || campaign.utm_medium || campaign.utm_campaign) && (
          <div className="mt-4 pt-3 border-t border-slate-800/80 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-500 font-medium flex items-center gap-1">
              <Tag className="w-3 h-3 text-slate-400" /> Presets:
            </span>
            {campaign.utm_source && (
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono text-[11px]">
                source={campaign.utm_source}
              </span>
            )}
            {campaign.utm_medium && (
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono text-[11px]">
                medium={campaign.utm_medium}
              </span>
            )}
            {campaign.utm_campaign && (
              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono text-[11px]">
                campaign={campaign.utm_campaign}
              </span>
            )}
          </div>
        )}
      </Card>

      {/* Campaign Associated Links Table */}
      <Card className="p-0 overflow-hidden">
        <CardHeader className="p-4 border-b border-slate-800">
          <div>
            <CardTitle>Campaign Links</CardTitle>
            <CardDescription>All shortened URLs associated with this marketing campaign</CardDescription>
          </div>
        </CardHeader>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-900/60 border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                <th className="py-3 px-4">Shortcode & Title</th>
                <th className="py-3 px-4">Destination Target</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-right">Clicks</th>
                <th className="py-3 px-4 text-right">Created</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {links.length > 0 ? (
                links.map((link) => (
                  <tr key={link.id} className="hover:bg-slate-900/40 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-orange-400">/{link.short_code}</span>
                        <button
                          onClick={() => handleCopy(link.short_code)}
                          className="text-slate-500 hover:text-slate-300 transition-colors cursor-pointer"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-slate-200 font-medium truncate max-w-[200px] mt-0.5">{link.title}</p>
                    </td>

                    <td className="py-3 px-4">
                      <a
                        href={link.destination_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-slate-200 flex items-center gap-1 font-mono truncate max-w-[240px]"
                      >
                        <span className="truncate">{link.destination_url}</span>
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    </td>

                    <td className="py-3 px-4 text-center">
                      <Badge variant={link.status === 'active' ? 'active' : 'disabled'}>{link.status}</Badge>
                    </td>

                    <td className="py-3 px-4 text-right font-mono font-bold text-slate-100">
                      {formatNumber(link.total_clicks)}
                    </td>

                    <td className="py-3 px-4 text-right text-slate-400 font-mono text-[11px]">
                      {formatDate(link.created_at)}
                    </td>

                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setQrModalData({ shortCode: link.short_code, title: link.title })}
                          className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors cursor-pointer"
                          title="Generate QR code"
                        >
                          <QrCode className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => onEditLink(link.id)}
                          className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors cursor-pointer"
                          title="Edit link"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">
                    No links associated with this campaign yet. Click "Add Link to Campaign" above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* QR Modal */}
      {qrModalData && (
        <QRCodeModal
          isOpen={!!qrModalData}
          onClose={() => setQrModalData(null)}
          shortCode={qrModalData.shortCode}
          linkTitle={qrModalData.title}
        />
      )}
    </div>
  );
};
